import type { FloatPoint2D } from '@/shared/api/protocol-ts/model/dto_common_geometry_pb';
import type { AnnotationsLayer } from '@/shared/api/protocol-ts/model/dto_common_image_annotations_pb';
import type { Annotation } from '@/shared/graphics/RenderComponents/AnnotationsLayer/Annotation';

import {
  makeFullHeadMprPointTransform,
  isSameFullHeadMprAnnotationPlane,
  type FullHeadMprAnnotationFrame,
} from '@/features/MPR';

import type { FullHeadMprSliceRecordView } from '../config';

type PointTransform = (point: { x: number; y: number }) => {
  x: number;
  y: number;
};

export const transformFullHeadMprSliceAnnotationLayers = ({
  layers,
  transform,
}: {
  layers: AnnotationsLayer[];
  transform: PointTransform;
}) =>
  layers.map((layer) => {
    const transformed = layer.clone();
    const transformPoint = (point?: FloatPoint2D) => {
      if (!point) {
        return;
      }
      const next = transform({ x: point.X, y: point.Y });
      point.X = next.x;
      point.Y = next.y;
    };
    transformed.Annotations.forEach(({ Type }) => {
      switch (Type.case) {
        case 'Ruler':
        case 'Arrow':
        case 'Guide':
          transformPoint(Type.value.Start);
          transformPoint(Type.value.End);
          break;
        case 'Angle':
          transformPoint(Type.value.Start);
          transformPoint(Type.value.End);
          transformPoint(Type.value.Corner);
          break;
        case 'PolyLine':
          Type.value.Points.forEach(transformPoint);
          break;
      }
    });
    return transformed;
  });

export const transformFullHeadMprGraphicsAnnotations = ({
  annotations,
  transform,
}: {
  annotations: Annotation[];
  transform: PointTransform;
}): Annotation[] =>
  annotations.map((annotation) => {
    const first = transform({ x: annotation.x1, y: annotation.y1 });
    const second = transform({ x: annotation.x2, y: annotation.y2 });
    const base = {
      ...annotation,
      x1: first.x,
      y1: first.y,
      x2: second.x,
      y2: second.y,
    };
    if (annotation.kind === 'angle') {
      const third = transform({ x: annotation.x3, y: annotation.y3 });
      return { ...base, kind: 'angle', x3: third.x, y3: third.y };
    }
    return base;
  });

export const getFullHeadMprAnnotationFrame = (
  record?: FullHeadMprSliceRecordView,
): FullHeadMprAnnotationFrame | null => {
  const view =
    record?.editorState?.annotationView ??
    (record?.source.kind === 'view' ? record.source.view : null);
  return view && record?.geometry
    ? {
        heightPx: record.geometry.heightPx,
        widthPx: record.geometry.widthPx,
        view,
      }
    : null;
};

export const makeFullHeadMprDisplayAnnotations = ({
  record,
  to,
}: {
  record: FullHeadMprSliceRecordView;
  to: FullHeadMprAnnotationFrame;
}) => {
  const from = getFullHeadMprAnnotationFrame(record);
  if (
    !from ||
    !isSameFullHeadMprAnnotationPlane({
      isSeries: record.source.kind === 'series',
      left: from.view,
      right: to.view,
    })
  ) {
    return [];
  }
  return transformFullHeadMprGraphicsAnnotations({
    annotations: record.annotations,
    transform: makeFullHeadMprPointTransform({ from, to }),
  });
};
